import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_ME } from '../graphql/getMe';
import { GET_ALL_USERS } from '../graphql/getAllUsers';
import { GET_ALL_QUESTIONS } from '../graphql/getAllQuestions';
import { Role } from '../__generated__/graphql';
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Quiz as QuizIcon,
  LibraryBooks as LibraryBooksIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { AdminStyles } from '../styles/AdminStyles';
import { CommonStyles } from '../styles/CommonStyles';
import { useNavigateHome } from '../hooks/useNavigateHome';

const Admin = () => {
  const navigate = useNavigate();
  const { data: me, loading: meLoading } = useQuery(GET_ME);
  const { data: usersData, loading: usersLoading } = useQuery(GET_ALL_USERS);
  const { data: questionsData, loading: questionsLoading } =
    useQuery(GET_ALL_QUESTIONS);
  const { navigateHome } = useNavigateHome();

  if (meLoading || usersLoading || questionsLoading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  const totalUsers = usersData?.getAllUsers?.length || 0;
  const totalQuestions = questionsData?.getQuestionsPaginated?.totalCount || 0;
  const adminUsers =
    usersData?.getAllUsers?.filter((user: any) => user.role === Role.Admin)
      .length || 0;
  const regularUsers = totalUsers - adminUsers;

  const navigationCards = [
    {
      title: 'Manage Users',
      description:
        'View and manage all user accounts, reset fingerprints, and monitor user activity',
      icon: <PeopleIcon sx={AdminStyles.cardIcon} />,
      onClick: () => navigate('/admin/users'),
      color: '#2196F3',
    },
    {
      title: 'Question Bank',
      description:
        'Browse and manage the complete question database with filtering and search',
      icon: <LibraryBooksIcon sx={AdminStyles.cardIcon} />,
      onClick: () => navigate('/admin/question-bank'),
      color: '#4CAF50',
    },
    {
      title: 'Create Question',
      description:
        'Add new questions to the database with rich text formatting and multiple choice options',
      icon: <AddIcon sx={AdminStyles.cardIcon} />,
      onClick: () => navigate('/admin/questions/new'),
      color: '#FF9800',
    },
  ];

  return (
    <Box sx={AdminStyles.container}>
      {/* Welcome Section */}
      <Box sx={AdminStyles.welcomeSection}>
        {me?.getMe?.name && (
          <Typography sx={AdminStyles.title}>
            Welcome back, Hottie ❤️
          </Typography>
        )}
        <Typography sx={AdminStyles.subtitle}>
          Manage your board exam application with powerful admin tools and
          insights
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={AdminStyles.statsGrid}>
        <Card sx={AdminStyles.statCard}>
          <CardContent>
            <Typography sx={AdminStyles.statNumber}>{totalUsers}</Typography>
            <Typography sx={AdminStyles.statLabel}>Total Users</Typography>
          </CardContent>
        </Card>
        <Card sx={AdminStyles.statCard}>
          <CardContent>
            <Typography sx={AdminStyles.statNumber}>{regularUsers}</Typography>
            <Typography sx={AdminStyles.statLabel}>Regular Users</Typography>
          </CardContent>
        </Card>
        <Card sx={AdminStyles.statCard}>
          <CardContent>
            <Typography sx={AdminStyles.statNumber}>{adminUsers}</Typography>
            <Typography sx={AdminStyles.statLabel}>Admin Users</Typography>
          </CardContent>
        </Card>
        <Card sx={AdminStyles.statCard}>
          <CardContent>
            <Typography sx={AdminStyles.statNumber}>
              {totalQuestions}
            </Typography>
            <Typography sx={AdminStyles.statLabel}>Total Questions</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Navigation Cards */}
      <Box sx={AdminStyles.dashboardGrid}>
        {navigationCards.map((card, index) => (
          <Card
            key={index}
            sx={{
              ...AdminStyles.navigationCard,
              '&:hover': {
                ...AdminStyles.navigationCard['&:hover'],
                border: `2px solid ${card.color}`,
              },
            }}
            onClick={card.onClick}
          >
            <CardContent>
              {card.icon}
              <Typography sx={AdminStyles.cardTitle}>{card.title}</Typography>
              <Typography sx={AdminStyles.cardDescription}>
                {card.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default Admin;
